import React, {
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import AOS from "aos";
import { Route, Routes, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import routes from "./routes";
import Page404 from "../pages/404";
import Header from "../components/header/Header";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../features/auth/AuthProvider";
import { Navigate } from "react-router-dom";
import AgeVerificationModal from "../components/ageVerification/AgeVerificationModal";

function App() {
  const location = useLocation();
  const [direction, setDirection] = useState("right");
  const previousPathRef = useRef(location.pathname);
  const isMainMenuClickRef = useRef(false);
  const [shouldAnimate, setShouldAnimate] = useState(true);

  // AOS

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
    });
  }, []);


  // Scroll logic
  useLayoutEffect(() => {
    window.history.scrollRestoration = "manual";
    const handleScroll = () => {
      const previousPath = previousPathRef.current;
      const isMainMenuClick = isMainMenuClickRef.current;

      if (location.hash && location.pathname === previousPath) {
        setTimeout(() => {
          const targetElement = document.querySelector(location.hash);
          if (targetElement) {
            const headerOffset =
              document.querySelector("header")?.offsetHeight || 80;
            const elementPosition = targetElement.getBoundingClientRect().top;
            const offsetPosition =
              elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth",
            });
          }
        }, 50);
      } else if (location.pathname !== previousPath || isMainMenuClick) {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
        isMainMenuClickRef.current = false;
      }
    };

    const animationFrameId = requestAnimationFrame(handleScroll);
    return () => cancelAnimationFrame(animationFrameId);
  }, [location.pathname, location.hash]);

  // Route direction & animation control
  useEffect(() => {
    const previousPath = previousPathRef.current;
    const isSamePath = location.pathname === previousPath;
    setShouldAnimate(!isSamePath);

    if (!isSamePath) {
      const currentRouteIndex = routes.findIndex(
        (route) => route.path === location.pathname
      );
      const storedIndex = sessionStorage.getItem("previousRouteIndex");
      const previousRouteIndex = storedIndex ? parseInt(storedIndex, 10) : -1;

      if (currentRouteIndex !== -1) {
        const newDirection =
          currentRouteIndex > previousRouteIndex ? "right" : "left";
        setDirection(newDirection);
        sessionStorage.setItem(
          "previousRouteIndex",
          currentRouteIndex.toString()
        );
      }

      previousPathRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Detect main menu click
  useEffect(() => {
    const handleMainMenuClick = (e) => {
      const isMainMenuLink = e.target.closest(".menu > li > a");
      if (isMainMenuLink) {
        isMainMenuClickRef.current = true;
      }
    };

    document.addEventListener("click", handleMainMenuClick);
    return () => document.removeEventListener("click", handleMainMenuClick);
  }, []);

  // Animation variants
  const pageVariants = {
    initial: { x: direction === "right" ? "100vw" : "-100vw", opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { opacity: 0 },
  };

  const pageTransition = {
    type: "tween",
    duration: 0.2,
    ease: "easeInOut",
  };

  const RequireAuth = ({ children }) => {
    const { user } = useAuth();
    return user ? children : <Navigate to="/log-in" replace />;
  };

  const RequireAdmin = ({ children }) => {
    const { user } = useAuth();

    if (!user) {
      return <Navigate to="/log-in" replace />;
    }

    return user?.isAdmin ? children : <Navigate to="/" replace />;
  };

  const renderRouteElement = (route) => {
    if (route.isAdminOnly) {
      return <RequireAdmin>{route.component}</RequireAdmin>;
    }

    if (route.isPrivate) {
      return <RequireAuth>{route.component}</RequireAuth>;
    }

    return route.component;
  };

  const routeFallback = (
    <main
      style={{
        minHeight: "55vh",
        display: "grid",
        placeItems: "center",
        padding: "120px 20px",
        color: "#f5edf7",
      }}
      aria-live="polite"
    >
      <span>Loading...</span>
    </main>
  );

  const routeElements = (
    <Suspense fallback={routeFallback}>
      <Routes location={location}>
        {routes.map((data) => (
          <Route
            key={data.path}
            path={data.path}
            element={renderRouteElement(data)}
          />
        ))}
        <Route path="*" element={<Page404 />} />
      </Routes>
    </Suspense>
  );

  return (
    <>
      <AgeVerificationModal />
      <Header />
      <AnimatePresence mode="wait" initial={false}>
        {shouldAnimate ? (
          <motion.div
            key={location.pathname}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={pageVariants}
            transition={pageTransition}
          >
            {routeElements}
          </motion.div>
        ) : (
          <div key="static">{routeElements}</div>
        )}
      </AnimatePresence>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        style={{ zIndex: 99999 }}
      />
    </>
  );
}

export default App;
